<script setup>
/**
 * What you can make.
 *
 * Everything above this on the page answers "what needs me". This answers "what
 * can I make", and its only job is to remove the gap between deciding and
 * starting. A tile that drops you on a list page to hunt for a New button has
 * done nothing — the sidebar already did that. So every destination below lands
 * with the create form open and the first field focused, via `?create=`.
 *
 * Which tiles appear is the server's answer, not this component's: a tile is
 * only worth offering to somebody whose submit will not 403, and only the
 * server knows both the viewer's role and whether the feature is switched on at
 * all. A tile the viewer cannot use is absent, never disabled — there is
 * nothing to explain.
 */
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import IconDocument from '../../icons/IconDocument.vue';
import IconFunnel from '../../icons/IconFunnel.vue';
import IconMegaphone from '../../icons/IconMegaphone.vue';
import IconPaperPlane from '../../icons/IconPaperPlane.vue';
import IconUserAdd from '../../icons/IconUserAdd.vue';
import IconVideo from '../../icons/IconVideo.vue';
import XlStatusIcon from '../../xl/XlStatusIcon.vue';

const props = defineProps({
  // Keys, from `data.make.allowed`. Order here is ignored — the row renders in
  // the fixed order below, so the layout does not shuffle between roles.
  allowed: { type: Array, default: () => [] },
});

const router = useRouter();

/** The sub-line says what you get, not what the feature is called. */
const TILES = [
  {
    key: 'session',
    // A camera, not a calendar: this hub is a live video platform, and the
    // prototype's icon for this tile is a camcorder — a grid-and-rings
    // calendar glyph answers "when", not "what you are about to make".
    icon: IconVideo,
    title: 'Create a session',
    subline: 'One-off or recurring',
    to: { path: '/sessions/new' },
  },
  {
    key: 'message',
    icon: IconPaperPlane,
    title: 'Send a message',
    subline: 'One-off to a segment',
    // The compose form IS this tab's default state, so the tab alone lands
    // correctly; `create` marks that we arrived from here, for focus and for
    // the cancel path.
    to: { path: '/communications', query: { tab: 'send', create: 'message' } },
  },
  {
    key: 'banner',
    icon: IconMegaphone,
    title: 'Post a banner',
    subline: "Top of everyone's home",
    to: { path: '/content', query: { tab: 'banners', create: 'banner' } },
  },
  {
    key: 'resource',
    icon: IconDocument,
    title: 'Add a resource',
    subline: 'File or link',
    to: { path: '/content', query: { tab: 'resources', create: 'resource' } },
  },
  {
    key: 'segment',
    icon: IconFunnel,
    title: 'Build a segment',
    subline: 'Then message it',
    to: { path: '/communications', query: { tab: 'segments', create: 'segment' } },
  },
  {
    key: 'invite',
    icon: IconUserAdd,
    title: 'Invite people',
    subline: 'One or a list',
    to: { path: '/policyholders', query: { tab: 'registered', create: 'invite' } },
  },
];

const tiles = computed(() => TILES.filter((tile) => props.allowed.includes(tile.key)));

const open = (tile) => router.push(tile.to);
</script>

<template>
  <section v-if="tiles.length" class="make" data-testid="overview-make">
    <h2 class="make__title xl-section-heading">Make something</h2>

    <div class="make__grid">
      <!--
        A real button, so the whole tile is the target and Enter and Space come
        free rather than being hand-bound to a div pretending to be one.
      -->
      <button
        v-for="tile in tiles"
        :key="tile.key"
        type="button"
        class="make__tile"
        :data-testid="`make-${tile.key}`"
        @click="open(tile)"
      >
        <XlStatusIcon tone="brand">
          <component :is="tile.icon" color="currentColor" />
        </XlStatusIcon>

        <span class="make__text">
          <span class="make__tile-title">{{ tile.title }}</span>
          <span class="make__subline">{{ tile.subline }}</span>
        </span>
      </button>
    </div>
  </section>
</template>

<style scoped>
.make {
  margin-top: 20px;
}

.make__title {
  margin: 0 0 10px;
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.make__title::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--xl-color-border);
}

.make__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 620px) {
  .make__grid { grid-template-columns: 1fr; }
}

.make__tile {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  padding: 15px 17px;
  overflow: hidden;
  border: 1px solid var(--xl-color-border);
  border-radius: 18px;
  background: #fff;
  box-shadow: var(--xl-shadow-1);
  font-family: var(--xl-font-family);
  text-align: left;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;
}

.make__tile:hover {
  transform: translateY(-2px);
  box-shadow: var(--xl-shadow-2);
}

.make__tile:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: 2px;
}

.make__text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.make__tile-title {
  font-size: 14.5px;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: -0.015em;
  color: var(--xl-color-on-surface);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.make__subline {
  margin-top: 3px;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--xl-color-outline);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The lift is decoration; the tile still has to be operable without it. */
@media (prefers-reduced-motion: reduce) {
  .make__tile { transition: none; }
  .make__tile:hover { transform: none; }
}
</style>
